import React from 'react';
import {Flex, Text} from '@chakra-ui/react';
import {useUserControls} from '../AccountGrid.controlstate';
import {BooleanFilterControl} from './BooleanFilterControl';
import {NumberFilterControl} from './NumberFilterControl';

import _ from 'lodash';
import {CategoriesFilterControl} from './CategoriesFilterControl';
import {AudienceFilterControl} from './AudienceFilterControl';
import {ScoringSignalsFilterControl} from './ScoringSignalsFilterControl';
import {TagsFilterControl} from './TagsFilterControl';
import {SimilarAccountsFilterControl} from './SimilarAccountsFilterControl';
import {FilterSelect} from './FilterSelect';

export type FilterName =
  | 'signals'
  | 'categories'
  | 'audiences'
  | 'tags'
  | 'inCrm'
  | 'inSam'
  | 'employees'
  | 'similarAccounts';

const FilterColumnsOptions: Record<FilterName, {label: string; value: string}> =
  {
    signals: {
      label: 'Signals',
      value: 'signals',
    },
    categories: {
      label: 'Categories',
      value: 'categories',
    },
    audiences: {
      label: 'Audiences',
      value: 'audiences',
    },
    tags: {
      label: 'Tags',
      value: 'tags',
    },
    inCrm: {
      label: 'Included in CRM',
      value: 'inCrm',
    },
    inSam: {
      label: 'Included in SAM',
      value: 'inSam',
    },
    employees: {
      label: 'Employees',
      value: 'employees',
    },
    similarAccounts: {
      label: 'Similar Accounts',
      value: 'similarAccounts',
    },
  };

export const FilterControl = ({
  availableOptions,
  selectedFilterName,
  onSetFilterName,
  onRemove,
}: {
  availableOptions: FilterName[];
  selectedFilterName: FilterName | undefined;
  onSetFilterName: (columnType: FilterName | undefined) => void;
  onRemove?: () => void;
}) => {
  const {
    inCrm,
    setInCrm,
    inSam,
    setInSam,
    minEmployees,
    setMinEmployees,
    maxEmployees,
    setMaxEmployees,
    signals,
    setSignals,
    primaryCategory,
    setPrimaryCategory,
    audience,
    setAudience,
    tags: selectedTags,
    setTags: setSelectedTags,
    similarTo,
    setSimilarTo,
  } = useUserControls();

  const previousFilterColumnRef = React.useRef<FilterName | undefined>(
    selectedFilterName
  );

  const clearFilterValueFn: Record<FilterName, () => void> = {
    signals: () => setSignals([[]]),
    categories: () => setPrimaryCategory([]),
    audiences: () => setAudience([]),
    tags: () => setSelectedTags([]),
    inCrm: () => setInCrm(undefined),
    inSam: () => setInSam(undefined),
    employees: () => {
      setMinEmployees(undefined);
      setMaxEmployees(undefined);
    },
    similarAccounts: () => setSimilarTo([]),
  };

  const handleOnRemove = () => {
    if (selectedFilterName) {
      clearFilterValueFn[selectedFilterName]();
    }
    onRemove?.();
  };

  return (
    <Flex gap={2}>
      <Flex alignItems="center" gap={2} flex={1} alignSelf="flex-start">
        <Text color="kgray.300" width="48px">
          and
        </Text>
        <FilterSelect
          options={availableOptions.map((value) => FilterColumnsOptions[value])}
          value={
            selectedFilterName
              ? FilterColumnsOptions[selectedFilterName]
              : undefined
          }
          onChange={(e) => {
            const newFilterColumn = e?.value as FilterName;
            const prevFilterColumn = previousFilterColumnRef.current;

            // Clear the previous filter value if it changed
            if (prevFilterColumn && prevFilterColumn !== newFilterColumn) {
              clearFilterValueFn[prevFilterColumn]();
            }

            // Update the ref and notify parent
            previousFilterColumnRef.current = newFilterColumn;
            onSetFilterName(newFilterColumn);
          }}
        />
      </Flex>

      {selectedFilterName === 'signals' && (
        <ScoringSignalsFilterControl
          selectedSignals={signals}
          setSelectedSignals={setSignals}
          onRemove={handleOnRemove}
        />
      )}

      {selectedFilterName === 'audiences' && (
        <AudienceFilterControl
          audience={audience}
          setAudience={setAudience}
          onRemove={handleOnRemove}
        />
      )}

      {selectedFilterName === 'tags' && (
        <TagsFilterControl
          selectedTags={selectedTags}
          setSelectedTags={setSelectedTags}
          onRemove={handleOnRemove}
        />
      )}

      {selectedFilterName === 'categories' && (
        <CategoriesFilterControl
          primaryCategory={primaryCategory}
          setPrimaryCategory={setPrimaryCategory}
          onRemove={handleOnRemove}
        />
      )}

      {selectedFilterName === 'inCrm' && (
        <BooleanFilterControl
          value={inCrm}
          setValue={setInCrm}
          onRemove={handleOnRemove}
        />
      )}
      {selectedFilterName === 'inSam' && (
        <BooleanFilterControl
          value={inSam}
          setValue={setInSam}
          onRemove={handleOnRemove}
        />
      )}
      {selectedFilterName === 'employees' && (
        <NumberFilterControl
          min={minEmployees}
          max={maxEmployees}
          onMinChange={setMinEmployees}
          onMaxChange={setMaxEmployees}
          onRemove={handleOnRemove}
        />
      )}

      {selectedFilterName === 'similarAccounts' && (
        <SimilarAccountsFilterControl
          similarTo={similarTo}
          setSimilarTo={setSimilarTo}
          onRemove={handleOnRemove}
        />
      )}

      {selectedFilterName === undefined && (
        <BooleanFilterControl
          isDisabled
          value={undefined}
          setValue={() => {}}
          onRemove={handleOnRemove}
        />
      )}
    </Flex>
  );
};
